/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import moment from 'moment';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';

export default class TopNavigation extends Component {
  @action
  setGroupSelectorYear(value) {
    this.groupSelectorYear = value;
  }

  @action
  setQuickSearchText(value) {
    this.quickSearchText = value;
  }

  // Services
  @service
  features;

  @service
  groupYears;

  @service
  options;

  @service
  permissions;

  // NOTE: injected under a different local name - the `quickSearch` action
  // below is a sibling class member and would otherwise shadow the service.
  @service('quick-search')
  quickSearchService;

  @service
  session;

  @service
  store;

  @service
  students;

  @service
  zendesk;

  @tracked peopleSelectorOpen = false;

  get peopleSelectorClass() {
    return this.peopleSelectorOpen ? 'active' : '';
  }

  @tracked groupSelectorOpen = false;

  get groupSelectorClass() {
    let classes = [];
    if (this.groupSelectorOpen) {
      classes.push('active');
    }
    if (this.groupSelectorYear !== this.currentYear) {
      classes.push('non-current-year');
    }
    return classes.join(' ');
  }

  @computed('peopleSelectorOpen', 'students.all.[]')
  get people() {
    // Trigger students service to load all students if not already primed.
    // This property will then recalculate once they're loaded.
    this.get('students.all');
    return this.store.peekAll('person');
  }

  @tracked groupSelectorYear = moment().year();
  @tracked currentYear = moment().year();

  get groups() {
    if (!this.groupSelectorOpen) {
      // Early return so we don't risk fetching data when closed.
      return [];
    }

    return this.groupYears.groupsFor(this.groupSelectorYear, this.currentYear);
  }

  // numGroupChanges is used to trigger a recompute of `groups` when creating
  // or deleting a group.
  @tracked numGroupChanges = 0;

  @tracked
  searchOpen = false;
  @tracked
  quickSearchText = '';

  get helpWidgetOpen() {
    return this.zendesk.isOpen;
  }

  // Only read when the help widget is opened, so it needn't be reactive.
  get hasSupportAccess() {
    if (this.permissions.meet('urn:linced:meta:manager')) {
      // LINC-ED Staff get access, so that we can see what the users see.
      return true;
    }
    // `staffWithSupportAccess` is an async hasMany, so reading it hands back a
    // PromiseManyArray, which isn't iterable. The reference's ids are already
    // known from the school payload, so there's nothing to load or await.
    let school = this.session.school;
    if (!school) {
      return false;
    }
    return school
      .hasMany('supportPersonIds')
      .ids()
      .includes(this.session.person?.id);
  }

  @optionAlias('core:staffPermissions:hideNextYearsPlacementsForTeachers')
  hideNextYearsPlacementsForTeachers;

  @computed('session.scopes.[]')
  get hasAdminAccess() {
    return this.permissions.meet(Scopes.TENANT_ADMIN);
  }

  @computed(
    'hasAdminAccess',
    'hideNextYearsPlacementsForTeachers',
    'groupSelectorYear',
    'currentYear',
  )
  get disableNextYear() {
    if (this.groupSelectorYear === this.currentYear + 1) {
      return true;
    }
    if (this.groupSelectorYear === this.currentYear) {
      return !this.hasAdminAccess && this.hideNextYearsPlacementsForTeachers;
    }
    return false;
  }

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    // Close any dialogs whenever the route changes
    if (this._lastURL && this._lastURL !== this.currentURL) {
      this.groupSelectorOpen = false;
      this.searchOpen = false;
      this.peopleSelectorOpen = false;
      this.toggleScrolling(false);
    }
    this._lastURL = this.currentURL;
  }

  toggleScrolling(disable) {
    let body = document.querySelector('body');
    if (disable) {
      // Remove any duplicates
      let newClasses = body.className.replace(/ no-scroll/g, '');
      body.className = `${newClasses} no-scroll`;
    } else {
      body.className = body.className.replace(/ no-scroll/g, '');
    }
  }

  @action
  async switchSchool() {
    await this.session.switchSchool();
  }

  @action
  togglePeopleSelector() {
    this.groupSelectorOpen = false;
    this.toggleProperty('peopleSelectorOpen');

    this.toggleScrolling(this.peopleSelectorOpen);
  }

  // Called when a group badge is interacted with inside the group selector.
  @action
  onGroupCardClose() {
    this.groupSelectorOpen = false;
    this.toggleScrolling(false);
  }

  @action
  toggleGroupSelector() {
    this.peopleSelectorOpen = false;
    this.toggleProperty('groupSelectorOpen');

    this.toggleScrolling(this.groupSelectorOpen);

    if (this.groupSelectorOpen) {
      let thisYear = moment().year();
      // Update currentYear in case we've rolled over while the app's open.
      this.currentYear = thisYear;
      // Default back to current year when opened.
      this.groupSelectorYear = thisYear;
    }
  }

  @action
  onGroupCreated() {
    this.incrementProperty('numGroupChanges');
  }

  @action
  onGroupDeleted() {
    this.incrementProperty('numGroupChanges');
  }

  @action
  toggleSearch() {
    this.quickSearchText = '';
    this.searchOpen = !this.searchOpen;
    if (this.searchOpen) {
      let searchBox = this.element.querySelector('#top-quick-search input');
      if (searchBox) {
        searchBox.focus();
      }
    }
  }

  @action
  quickSearch() {
    if (this.quickSearchText) {
      let searchText = this.quickSearchText;
      this.quickSearchText = '';

      this.quickSearchService.search(searchText);
    }
    this.searchOpen = false;
  }

  @action
  toggleHelpWidget() {
    if (this.zendesk.isOpen) {
      this.zendesk.close();
      return;
    }

    let name = this.get('session.person.fullName');
    let email = this.get('session.user.username');
    this.zendesk.open({
      // Only users with support access get the contact form.
      suppressContactForm: !this.hasSupportAccess,
      name: isEmpty(name) ? '' : name,
      email: isEmpty(email) ? '' : email,
    });
  }
}
